// ---------------------------------------------------------------------------
// Auth storage — single account, cookie-backed
// ---------------------------------------------------------------------------
//
// Storage layout:
//
//   Cookie: fwd_token
//     The active JWT, stored as a JS-readable cookie (NOT HttpOnly).
//     Shared across every tab in the browser — this is the whole point: a user
//     who opens the app in a second tab is already signed in, and closing one
//     tab no longer wipes that session.
//
//     Security: SameSite=Strict blocks CSRF; Secure (on HTTPS) blocks plaintext
//     leakage; path=/ scopes it to the app. It is readable by JS, exactly as
//     the previous sessionStorage token was — this change does not widen the
//     XSS surface, it only changes the scope from per-tab to per-browser.
//
//     Expiry: Max-Age matched to the backend's REFRESH_TOKEN_EXPIRE_DAYS (7 by
//     default), so the session survives a browser restart instead of dying with
//     the window. The backend's HttpOnly session_id cookie remains the real
//     authority — it carries the same lifetime and the access token is
//     refreshed for as long as it is valid. Every refresh rewrites this cookie,
//     so the window slides forward while the user stays active, and both sides
//     expire together for one who does not.
//
// DISABLED: "Add another account" / connected accounts (multi-account
// switching). All of that code is preserved below, commented out, so the
// feature can be restored by uncommenting it together with its backend
// counterparts. Nothing writes the old per-user cookies (fwd_token_<userId>),
// the account metadata list (fwd_accounts) or the add-account intent keys any
// more; clearLegacyTokenStorage()/clearAllAuth() still purge them so users
// upgrading from the old build don't keep dead entries around forever.
//
// Sessions from the pre-cookie build are NOT migrated — those users are logged
// out once and sign in again.
// ---------------------------------------------------------------------------

const TOKEN_COOKIE_KEY = "fwd_token";

/**
 * Cookie lifetime, in days. Mirrors the backend's REFRESH_TOKEN_EXPIRE_DAYS
 * (app/config/base.py), which governs how long the session actually stays
 * valid — the backend refreshes the access token for as long as its HttpOnly
 * session_id cookie lives. Keep the two in step: a longer value here leaves the
 * user looking signed in with a session the server has already expired.
 */
const SESSION_EXPIRE_DAYS = 7;

const SESSION_MAX_AGE_SECONDS = SESSION_EXPIRE_DAYS * 24 * 60 * 60;

/** Legacy keys — only ever read/removed, never written. Cleanup on upgrade. */
const LEGACY_ACCOUNTS_META_KEY = "fwd_accounts";
const LEGACY_USER_COOKIE_PREFIX = "fwd_token_";

// DISABLED (multi-account): storage keys for the add-another-account flow.
// const ACCOUNTS_META_KEY = "fwd_accounts";
// const USER_COOKIE_PREFIX = "fwd_token_";
// const ADD_ACCOUNT_CONNECT_TO_KEY = "fwd_add_account_connect_to";
// const ADD_ACCOUNT_EXPECTED_TARGET_USER_KEY = "fwd_add_account_expected_target_user_id";
// const ADD_ACCOUNT_INTENT_KEY = "fwd_add_account_intent";
// const ADD_ACCOUNT_WINDOW_NAME_PREFIX = "fwd_add_account:";

/**
 * DISABLED (multi-account):
 * Workspace linking only happens when the backend receives connect_to_user_id on
 * signup/login/link flows. That ID is included only from the “Add another account” tab:
 * fwd_add_account is tab-scoped sessionStorage + fwd_add_account_connect_to.
 * — A normal login/signup tab never sends connect_to_user_id → user is not added to a group.
 * — Completing magic link / OAuth on another browser still links: connect_to_user_id lives in the
 *   emailed token payload (Redis/JWT metadata), not in that browser’s storage.
 */

/** Same-origin tabs: notify others when one tab signs out. */
export const AUTH_LOGOUT_CHANNEL = "fwd_auth_logout";

export type LogoutBroadcastMessage = { type: "logout" };

export function broadcastLogout(): void {
  if (typeof BroadcastChannel === "undefined") return;
  const ch = new BroadcastChannel(AUTH_LOGOUT_CHANNEL);
  ch.postMessage({ type: "logout" } satisfies LogoutBroadcastMessage);
  ch.close();
}

// DISABLED (multi-account): logout_all broadcast a per-user logout to sibling
// tabs running *different* accounts. With one shared cookie there is only one
// session, so broadcastLogout() covers every tab.
// export function broadcastLogoutAll(): void {
//   if (typeof BroadcastChannel === "undefined") return;
//   const ch = new BroadcastChannel(AUTH_LOGOUT_CHANNEL);
//   ch.postMessage({ type: "logout_all" } satisfies LogoutBroadcastMessage);
//   ch.close();
// }

// In-memory cache to avoid re-parsing document.cookie on every axios request
let inMemoryToken: string | null = null;

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** Display metadata for one account. No token stored here. */
export interface AccountMeta {
  userId: string;
  email: string;
  fullName: string;
  role: string;
  roles: string[];
  profilePictureUrl: string | null;
}

// DISABLED (multi-account): the switcher's per-slot view model.
// /**
//  * What the UI sees — AccountMeta plus session-state flags.
//  * index 0 = self (the active user in this tab).
//  */
// export interface AccountSlot extends AccountMeta {
//   index: number;
//   /** true = this is the active user in this tab (index 0) */
//   hasToken: boolean;
//   /** JWT — non-empty only for the active slot */
//   token: string;
//   /**
//    * true  = cookie fwd_token_<userId> exists in this browser.
//    *         User has logged in here and hasn't explicitly signed out.
//    *         Clicking the slot switches to that account in the current tab.
//    * false = cookie absent — never connected on this device. Show "Sign in".
//    */
//   hasStoredToken: boolean;
// }

// ---------------------------------------------------------------------------
// Cookie helpers
// ---------------------------------------------------------------------------

/**
 * `Secure` is required before browsers will accept the cookie over HTTPS, but
 * it must be omitted on plain-HTTP localhost or the cookie is silently dropped
 * and the user can never sign in during local dev.
 */
const isSecureContext = (): boolean =>
  typeof window !== "undefined" && window.location.protocol === "https:";

/**
 * @param persistent when true, adds Max-Age so the cookie survives a browser
 * restart. Deletion passes false — a delete uses its own `max-age=0`.
 */
const cookieAttributes = (persistent = false): string =>
  [
    "path=/",
    "SameSite=Strict",
    ...(isSecureContext() ? ["Secure"] : []),
    ...(persistent ? [`Max-Age=${SESSION_MAX_AGE_SECONDS}`] : []),
  ].join("; ");

const readCookie = (name: string): string | null => {
  const prefix = `${name}=`;
  const match = document.cookie.split("; ").find((c) => c.startsWith(prefix));
  if (!match) return null;
  try {
    return decodeURIComponent(match.slice(prefix.length));
  } catch {
    return null;
  }
};

const deleteCookie = (name: string): void => {
  document.cookie = `${name}=; max-age=0; ${cookieAttributes()}`;
};

// ---------------------------------------------------------------------------
// Access token — browser-scoped (cookie), shared by every tab
// ---------------------------------------------------------------------------

export const getAccessToken = (): string | null => {
  if (inMemoryToken) return inMemoryToken;
  const t = readCookie(TOKEN_COOKIE_KEY);
  if (t) inMemoryToken = t;
  return t;
};

export const setSessionToken = (token: string): void => {
  inMemoryToken = token;
  // Persistent: survives a browser restart, matching the backend session.
  // Every refresh rewrites this, so the window slides forward while the user
  // stays active — same behaviour as the backend's session_id cookie.
  document.cookie = `${TOKEN_COOKIE_KEY}=${encodeURIComponent(token)}; ${cookieAttributes(true)}`;
};

export const clearSessionToken = (): void => {
  inMemoryToken = null;
  deleteCookie(TOKEN_COOKIE_KEY);
};

export const isAuthenticated = (): boolean => !!getAccessToken();

// ---------------------------------------------------------------------------
// DISABLED (multi-account): per-user token cookies
// ---------------------------------------------------------------------------
//
// These backed account switching: each linked user kept their own JWT cookie so
// the switcher could activate one without a fresh login. With a single shared
// fwd_token cookie there is exactly one session, so none of this is reachable.
//
// /** Write fwd_token_<userId> cookie. */
// export const storeUserToken = (userId: string, token: string): void => {
//   const name = `${USER_COOKIE_PREFIX}${userId}`;
//   document.cookie = `${name}=${encodeURIComponent(token)}; path=/; SameSite=Strict`;
// };
//
// /** Read fwd_token_<userId> cookie. Returns null if absent or expired. */
// export const getUserToken = (userId: string): string | null => {
//   const name = `${USER_COOKIE_PREFIX}${userId}`;
//   const match = document.cookie
//     .split("; ")
//     .find((c) => c.startsWith(`${name}=`));
//   if (!match) return null;
//   try {
//     return decodeURIComponent(match.slice(name.length + 1));
//   } catch {
//     return null;
//   }
// };
//
// /** Delete fwd_token_<userId> cookie (called on explicit logout). */
// export const removeUserToken = (userId: string): void => {
//   document.cookie = `${USER_COOKIE_PREFIX}${userId}=; max-age=0; path=/; SameSite=Strict`;
// };
//
// /** true = the per-user cookie exists (user has an active or recoverable session). */
// export const hasUserToken = (userId: string): boolean =>
//   getUserToken(userId) !== null;

// ---------------------------------------------------------------------------
// DISABLED (multi-account): account metadata list — localStorage
// ---------------------------------------------------------------------------
//
// Cached display info (name, email, role, picture) for every linked account so
// the switcher could render rows without refetching. A single-account app reads
// all of this straight from the authenticated user instead.
//
// export const getAccountsMeta = (): AccountMeta[] => {
//   try {
//     const raw = localStorage.getItem(ACCOUNTS_META_KEY);
//     return raw ? (JSON.parse(raw) as AccountMeta[]) : [];
//   } catch {
//     return [];
//   }
// };
//
// const saveAccountsMeta = (accounts: AccountMeta[]): void => {
//   localStorage.setItem(ACCOUNTS_META_KEY, JSON.stringify(accounts));
// };
//
// /** Upsert display metadata for one user. Never touches tokens or cookies. */
// export const upsertAccountMeta = (meta: AccountMeta): void => {
//   const accounts = getAccountsMeta();
//   const idx = accounts.findIndex((a) => a.userId === meta.userId);
//   if (idx !== -1) {
//     accounts[idx] = meta;
//   } else {
//     accounts.push(meta);
//   }
//   saveAccountsMeta(accounts);
// };
//
// /** Remove display metadata for one user. */
// export const removeAccountMeta = (userId: string): void => {
//   saveAccountsMeta(getAccountsMeta().filter((a) => a.userId !== userId));
// };
//
// /** Replace the full metadata list. */
// export const setAccountsMeta = (accounts: AccountMeta[]): void => {
//   saveAccountsMeta(accounts);
// };

// ---------------------------------------------------------------------------
// DISABLED (multi-account): add-account mode flag & one-time intent
// ---------------------------------------------------------------------------
//
// "Add Another Account" opened /login in a new tab carrying a nonce in
// window.name. That tab claimed the intent, marked itself as add-account mode,
// and passed connect_to_user_id to the backend so the two users were linked
// into one workspace group. Removing the feature means never setting the flag,
// so no auth flow sends connect_to_user_id and no linking occurs.
//
// export const markAddAccountMode = (): void => {
//   // sessionStorage: scoped to the tab that was opened for add-account.
//   // This prevents the flag from leaking into unrelated tabs or normal login flows.
//   sessionStorage.setItem("fwd_add_account", "add_account");
// };
//
// /**
//  * Create a one-time add-account intent in localStorage and return a nonce.
//  * The newly opened /login tab must claim this intent using its window.name.
//  */
// export const createAddAccountIntent = (parentUserId: string): string => {
//   const nonce = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
//   const expiresAt = Date.now() + 2 * 60 * 1000; // 2 minutes
//   localStorage.setItem(
//     ADD_ACCOUNT_INTENT_KEY,
//     JSON.stringify({ nonce, parentUserId, expiresAt })
//   );
//   return nonce;
// };
//
// export const setAddAccountExpectedTargetUserId = (userId?: string): void => {
//   if (!userId) {
//     localStorage.removeItem(ADD_ACCOUNT_EXPECTED_TARGET_USER_KEY);
//     return;
//   }
//   localStorage.setItem(ADD_ACCOUNT_EXPECTED_TARGET_USER_KEY, userId);
// };
//
// export const getAddAccountExpectedTargetUserId = (): string | undefined => {
//   if (!isAddAccountMode()) return undefined;
//   const v = localStorage.getItem(ADD_ACCOUNT_EXPECTED_TARGET_USER_KEY);
//   return v || undefined;
// };
//
// /**
//  * Claim add-account intent in the current tab using window.name.
//  * Returns true only when a valid one-time intent is consumed.
//  */
// export const claimAddAccountIntentFromWindowName = (windowName: string): boolean => {
//   if (!windowName.startsWith(ADD_ACCOUNT_WINDOW_NAME_PREFIX)) return false;
//   const nonce = windowName.slice(ADD_ACCOUNT_WINDOW_NAME_PREFIX.length);
//   if (!nonce) return false;
//
//   try {
//     const raw = localStorage.getItem(ADD_ACCOUNT_INTENT_KEY);
//     if (!raw) return false;
//     const parsed = JSON.parse(raw) as {
//       nonce?: string;
//       parentUserId?: string;
//       expiresAt?: number;
//     };
//     if (
//       parsed.nonce !== nonce ||
//       !parsed.parentUserId ||
//       !parsed.expiresAt ||
//       Date.now() > parsed.expiresAt
//     ) {
//       localStorage.removeItem(ADD_ACCOUNT_INTENT_KEY);
//       return false;
//     }
//
//     markAddAccountMode();
//     localStorage.setItem(ADD_ACCOUNT_CONNECT_TO_KEY, parsed.parentUserId);
//     // Keep any expected target selected by the opener; do not set one here.
//     localStorage.removeItem(ADD_ACCOUNT_INTENT_KEY);
//     return true;
//   } catch {
//     localStorage.removeItem(ADD_ACCOUNT_INTENT_KEY);
//     return false;
//   }
// };
//
// export const clearAddAccountMode = (): void => {
//   sessionStorage.removeItem("fwd_add_account");
//   localStorage.removeItem(ADD_ACCOUNT_CONNECT_TO_KEY);
//   localStorage.removeItem(ADD_ACCOUNT_EXPECTED_TARGET_USER_KEY);
//   localStorage.removeItem(ADD_ACCOUNT_INTENT_KEY);
// };
//
// /**
//  * Remember which user initiated "Add Another Account".
//  * The fresh /login tab has no JWT so magic-link / signup / LinkedIn use this
//  * to pass connect_to_user_id to the backend.
//  */
// export const setAddAccountLinkParentUserId = (userId: string): void => {
//   localStorage.setItem(ADD_ACCOUNT_CONNECT_TO_KEY, userId);
// };
//
// export const getAddAccountConnectTargetUserId = (): string | undefined => {
//   if (!isAddAccountMode()) return undefined;
//   const stored = localStorage.getItem(ADD_ACCOUNT_CONNECT_TO_KEY);
//   if (stored) return stored;
//   try {
//     const token = getAccessToken();
//     if (token) {
//       const payload = JSON.parse(atob(token.split(".")[1]));
//       if (payload?.sub) return String(payload.sub);
//     }
//   } catch { /* */ }
//   return undefined;
// };

/**
 * Always false — the add-account flow is disabled.
 *
 * Kept as a live function (rather than commented out) because OnboardingGuard
 * and the auth flows branch on it. Returning a constant keeps those call sites
 * readable and makes restoring the feature a one-line change here.
 */
export const isAddAccountMode = (): boolean => false;

// ---------------------------------------------------------------------------
// Legacy storage cleanup + one-time migration
// ---------------------------------------------------------------------------

/** Remove every storage key and cookie left over from multi-account support. */
const purgeLegacyMultiAccountStorage = (): void => {
  try {
    localStorage.removeItem(LEGACY_ACCOUNTS_META_KEY);
    localStorage.removeItem("connected_accounts");
    localStorage.removeItem("fwd_add_account");
    localStorage.removeItem("fwd_add_account_connect_to");
    localStorage.removeItem("fwd_add_account_expected_target_user_id");
    localStorage.removeItem("fwd_add_account_intent");
    localStorage.removeItem("user_tracking_info");
    localStorage.removeItem("active_account_index");
    sessionStorage.removeItem("fwd_add_account");
    sessionStorage.removeItem("active_account_index");

    Object.keys(localStorage)
      .filter((k) => k.startsWith("fwd_handoff_") || k.startsWith(LEGACY_USER_COOKIE_PREFIX))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    /* storage can throw in private modes — cleanup is best-effort */
  }

  // Per-user token cookies from the multi-account build.
  document.cookie.split("; ").forEach((c) => {
    const name = c.split("=")[0];
    if (name.startsWith(LEGACY_USER_COOKIE_PREFIX)) deleteCookie(name);
  });
};

/**
 * Clear storage written by pre-cookie builds. Runs once on app start, before
 * any auth check.
 *
 * Deliberately does NOT carry an old token over into the cookie: anyone signed
 * in under the previous build is simply logged out and signs in again. Dropping
 * the old keys still matters, so a stale sessionStorage["fwd_token"] can never
 * shadow the cookie or be mistaken for a live session.
 */
export const clearLegacyTokenStorage = (): void => {
  try {
    sessionStorage.removeItem(TOKEN_COOKIE_KEY);
    localStorage.removeItem("access_token");
  } catch {
    /* storage can throw in private modes — cleanup is best-effort */
  }

  purgeLegacyMultiAccountStorage();
};

// ---------------------------------------------------------------------------
// Full sign-out
// ---------------------------------------------------------------------------

export const clearAllAuth = (): void => {
  clearSessionToken();
  try {
    sessionStorage.removeItem(TOKEN_COOKIE_KEY);
    localStorage.removeItem("access_token");
  } catch {
    /* ignore */
  }
  purgeLegacyMultiAccountStorage();
};

// ---------------------------------------------------------------------------
// Token payload helpers
// ---------------------------------------------------------------------------

export const getUserTrackingInfo = (): { id: string; role: string } | null => {
  const token = getAccessToken();
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload?.sub ? { id: String(payload.sub), role: String(payload.role ?? "") } : null;
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------------------
// Legacy shims — keep existing callers compiling
// ---------------------------------------------------------------------------

/** @deprecated use clearAllAuth() */
export const removeAllAccounts = clearAllAuth;
/** @deprecated use clearAllAuth() */
export const removeTokens = clearAllAuth;
/** @deprecated no-op — tracking info is derived from the JWT */
export const setUserTrackingInfo = (_id: string, _role: string): void => {};
/** @deprecated use clearLegacyTokenStorage() */
export const purgeLegacyActiveIndexStorage = clearLegacyTokenStorage;
/** @deprecated use setSessionToken directly */
export const updateActiveToken = (token: string): void => setSessionToken(token);

// DISABLED (multi-account): shims that returned slots for the account switcher.
// Every one of these depended on the accounts metadata list or the per-user
// token cookies, both of which are gone. Restore alongside the blocks above.
//
// /** @deprecated returns best-effort active meta */
// export const getActiveAccount = (): AccountSlot | null => {
//   const token = getAccessToken();
//   if (!token) return null;
//   try {
//     const payload = JSON.parse(atob(token.split(".")[1]));
//     const userId = String(payload?.sub ?? "");
//     if (!userId) return null;
//     const accounts = getAccountsMeta();
//     const idx = accounts.findIndex((a) => a.userId === userId);
//     const meta = accounts[idx];
//     if (!meta) return null;
//     return { ...meta, index: Math.max(idx, 0), hasToken: true, token, hasStoredToken: true };
//   } catch {
//     return null;
//   }
// };
//
// /** @deprecated use accounts from AuthContext */
// export const getAccounts = (): AccountSlot[] => {
//   const token = getAccessToken();
//   let activeUserId = "";
//   try {
//     if (token) {
//       const payload = JSON.parse(atob(token.split(".")[1]));
//       activeUserId = String(payload?.sub ?? "");
//     }
//   } catch { /* */ }
//   return getAccountsMeta().map((meta, idx) => ({
//     ...meta,
//     index: idx,
//     hasToken: meta.userId === activeUserId,
//     token: meta.userId === activeUserId ? (token ?? "") : "",
//     hasStoredToken: hasUserToken(meta.userId),
//   }));
// };
//
// /** @deprecated no-op */
// export const getActiveIndex = (): number => 0;
// /** @deprecated no-op */
// export const setActiveIndex = (_n: number): void => {};
// /** @deprecated no-op */
// export const saveAccounts = (_slots: AccountSlot[]): void => {};
// /** @deprecated no-op */
// export const switchToAccount = (_index: number): AccountSlot | null => null;
// /** @deprecated no-op */
// export const expireAccountSlot = (_index: number): AccountSlot | null => null;
// /** @deprecated no-op */
// export const prepareOpenAsUser = (_userId: string): string => "";
// /** @deprecated no-op */
// export const consumeOpenAsUser = (): string | null => null;
// /** @deprecated no-op — no longer URL-based switching */
// export const buildSwitchUrl = (basePath: string, _userId: string): string => basePath;
// /** @deprecated no-op */
// export const consumeSwitchToParam = (): string | null => null;
// /** @deprecated alias */
// export const storeHandoffToken = storeUserToken;
// /** @deprecated alias */
// export const consumeHandoffToken = (userId: string): string | null => getUserToken(userId);
//
// /** Upsert compatibility shim. */
// export const upsertAccountSlot = (
//   data: Omit<AccountSlot, "index" | "hasToken" | "hasStoredToken"> | (AccountMeta & { token: string }),
//   _options?: { makeActive?: boolean }
// ): AccountSlot => {
//   const meta: AccountMeta = {
//     userId: data.userId,
//     email: data.email,
//     fullName: data.fullName,
//     role: data.role,
//     roles: data.roles,
//     profilePictureUrl: data.profilePictureUrl,
//   };
//   upsertAccountMeta(meta);
//   if ("token" in data && data.token) {
//     setSessionToken(data.token);
//     storeUserToken(data.userId, data.token);
//   }
//   const accounts = getAccountsMeta();
//   const idx = accounts.findIndex((a) => a.userId === data.userId);
//   return {
//     ...meta,
//     index: Math.max(idx, 0),
//     hasToken: true,
//     token: ("token" in data ? data.token : "") ?? "",
//     hasStoredToken: true,
//   };
// };
